import React, { ReactNode, useEffect, useRef } from 'react'
import { useLocation } from 'react-router'
import { UserProvider } from 'context/user'
import { SocketTokenProvider } from 'context/socketToken'
import { EventsListProvider } from 'context/eventsList'
import { PlanProvider, useEntitlements } from 'context/plan'
import Routing from 'components/Routing'
import { useAuth0, User } from '@auth0/auth0-react'
import { LoadingPage } from './Loading'
import { VerifyGatePage, VerifyGateState } from 'components/VerifyGatePage'
import { isOverlayPath, isShopifyConnectedPath, ROOT_PATH, showsPublicHome } from 'lib/paths'
import { resumesSignInAtRoot, SESSION_HINT_AT_LOAD } from 'lib/authSessionHint'
import { PublicHomePage } from 'pages/PublicHomePage'
import { useAppFont } from 'hooks/useAppFont'
import { onOverlayPagePath } from 'components/overlayPreview/previewSeam'

const ResumeSignIn = (): ReactNode => {
  const { loginWithRedirect } = useAuth0()
  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    void loginWithRedirect({ appState: { returnTo: ROOT_PATH } })
  }, [loginWithRedirect])
  return <LoadingPage />
}

const verifyGateFor = (isAuthenticated: boolean, user: User | undefined): VerifyGateState | null => {
  if (!isAuthenticated || user === undefined) return null
  if (user.email_verified === false) return 'unverified'
  const hasRole = user.superuser === true || user.admin === true || user.streamer === true
  if (user.email_verified === true && !hasRole) return 'no-role'
  return null
}

const SuspendedGate = ({ children }: { children: ReactNode }): ReactNode => {
  const { suspended, staffUncapped } = useEntitlements()
  if (suspended && !staffUncapped) {
    return (
      <div
        data-testid='suspended-gate-page'
        className='flex min-h-screen items-center justify-center bg-dm-bg-main px-4 text-dm-text-primary'
      >
        <div className='w-full max-w-md rounded-xl border border-white/5 bg-dm-bg-secondary p-8 text-center'>
          <h1 className='text-xl font-semibold text-dm-text-primary'>
            This account is suspended
          </h1>
          <p className='mt-3 text-sm text-dm-text-secondary'>
            Contact support if you believe this is a mistake.
          </p>
        </div>
      </div>
    )
  }
  return <>{children}</>
}

export default function App (): ReactNode {
  const { isLoading, isAuthenticated, user, error } = useAuth0()
  const { pathname } = useLocation()

  useAppFont(!onOverlayPagePath(pathname))

  if (
    isOverlayPath(pathname) ||
    isShopifyConnectedPath(pathname)
  ) return <Routing />

  if (isLoading) return <LoadingPage />

  if (showsPublicHome({ pathname, isLoading, isAuthenticated })) {
    return resumesSignInAtRoot({ hadSessionHint: SESSION_HINT_AT_LOAD, signInFailed: error != null })
      ? <ResumeSignIn />
      : <PublicHomePage />
  }

  const gate = verifyGateFor(isAuthenticated, user)
  if (gate !== null) return <VerifyGatePage state={gate} />

  return (
    <UserProvider>
      <SocketTokenProvider>
        <EventsListProvider>
          <PlanProvider>
            <SuspendedGate>
              <Routing />
            </SuspendedGate>
          </PlanProvider>
        </EventsListProvider>
      </SocketTokenProvider>
    </UserProvider>
  )
}
